import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { loadCustomer, loadCustomerTypes, loadThirdParties } from '@sophocles/features/customers/api';
import { CustomerSchema, CustomerThirdPartySchema, CustomerTypeSchema } from '@sophocles/features/customers/schema';
import { normalize } from 'normalizr';

const customerEntityAdapter = createEntityAdapter();
const customerPreferencesEntityAdapter = createEntityAdapter();
const customerOptionsEntityAdapter = createEntityAdapter();
const customerTypeEntityAdapter = createEntityAdapter();
const customerThirdPartyEntityAdapter = createEntityAdapter();

const customerSlice = createSlice({
  name: 'customers',
  initialState: {
    customers: customerEntityAdapter.getInitialState({}),
    customerPreferences: customerPreferencesEntityAdapter.getInitialState({}),
    customerOptions: customerOptionsEntityAdapter.getInitialState({}),
    customerTypes: customerTypeEntityAdapter.getInitialState({}),
    customerThirdParties: customerThirdPartyEntityAdapter.getInitialState({}),
    currentCustomerId: null,
    lastLoaded: null,
    view: {
      search: null,
      page: 0,
      pageSize: 50,
      order: 'asc',
      orderBy: 'id',
    },
  },
  reducers: {
    setCurrentCustomerId(state, action) {
      state.currentCustomerId = action.payload;
    },
    setLastLoaded(state, action) {
      state.lastLoaded = action.payload;
    },
    setPage(state, action) {
      state.view.page = action.payload;
    },
    setPageSize(state, action) {
      state.view.pageSize = action.payload;
    },
    setSearch(state, action) {
      state.view.search = action.payload;
    },
    setOrder(state, action) {
      state.view.order = action.payload;
    },
    setOrderBy(state, action) {
      state.view.orderBy = action.payload;
    },
    receiveCustomers(state, action) {
      const { customers, customerPreferences, customerOptions } = action.payload;
      if (customers) {
        customerEntityAdapter.upsertMany(state.customers, customers);
      }
      if (customerPreferences) {
        customerPreferencesEntityAdapter.upsertMany(state.customerPreferences, customerPreferences);
      }
      if (customerOptions) {
        customerOptionsEntityAdapter.upsertMany(state.customerOptions, customerOptions);
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadCustomerTypes.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload.results, [CustomerTypeSchema]);
        customerTypeEntityAdapter.upsertMany(state.customerTypes, entities.customerTypes ?? {});
      })
      .addCase(loadCustomer.fulfilled, (state, action) => {
        const { entities, result } = normalize(action.payload, CustomerSchema);
        const customer = entities.customers[result];
        const preferences = entities.customerPreferences[customer.preferences];
        const options = entities.customerOptions[customer.options];
        customerEntityAdapter.upsertOne(state.customers, customer);
        customerPreferencesEntityAdapter.upsertOne(state.customerPreferences, preferences);
        customerOptionsEntityAdapter.upsertOne(state.customerOptions, options);
      })
      .addCase(loadThirdParties.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload, [CustomerThirdPartySchema]);
        // normalize omits the key entirely for an empty result set, and
        // upsertMany throws on undefined.
        customerThirdPartyEntityAdapter.upsertMany(state.customerThirdParties, entities.customerThirdParties ?? {});
      });
  },
});

export const selectCustomerTypeEntities = state => {
  return state.customers.customerTypes;
};

export const selectThirdPartyEntities = state => {
  return state.customers.customerThirdParties;
};

export const selectCustomerEntities = state => {
  return state.customers.customers;
};

export const selectCustomerPreferencesEntities = state => {
  return state.customers.customerPreferences;
};

export const selectCustomerOptionsEntities = state => {
  return state.customers.customerOptions;
};

export const selectCustomerView = state => {
  return state.customers.view;
};

// Customer Type Selectors

export const { selectById: selectCustomerTypeById, selectAll: selectAllCustomerTypes } =
  customerTypeEntityAdapter.getSelectors(selectCustomerTypeEntities);

// Third Party Selectors

export const { selectById: selectThirdPartyById, selectAll: selectAllThirdParties } =
  customerThirdPartyEntityAdapter.getSelectors(selectThirdPartyEntities);

export const selectThirdPartiesByCustomerId = customerId =>
  createSelector([selectAllThirdParties], thirdParties => thirdParties.filter(tp => tp.owner?.id === customerId));

// Customer Selectors

export const { selectById: selectCustomerById, selectAll: selectAllCustomers } =
  customerEntityAdapter.getSelectors(selectCustomerEntities);

export const selectDenormalizedCustomerById = createSelector(
  selectCustomerById,
  selectCustomerPreferencesEntities,
  selectCustomerOptionsEntities,
  (customer, preferences, options) => {
    return {
      ...customer,
      preferences: preferences[customer.preferences],
      options: options[customer.options],
    };
  },
);

export const selectCurrentCustomerId = state => {
  return state.customers.currentCustomerId;
};

export const selectCurrentCustomer = state => {
  return selectDenormalizedCustomerById(state, selectCurrentCustomerId(state));
};

export const selectCustomerLastLoaded = state => {
  return state.customers.lastLoaded;
};

const { reducer } = customerSlice;

export const {
  receiveCustomers,
  setCurrentCustomerId,
  setLastLoaded,
  setPage,
  setPageSize,
  setSearch,
  setOrder,
  setOrderBy,
} = customerSlice.actions;

export default reducer;
